import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ChevronDown, ChevronRight, Search, Save, Mail, CheckCircle, XCircle, RotateCcw, Loader2, Eye, X, FileDown, Lock, Unlock } from "lucide-react";
import { EstimateGrid } from "@/components/proposals/grid/EstimateGrid";
import { DEFAULT_COLS } from "@/components/proposals/grid/gridMath";
import { createMasterPart } from "@/lib/api";

const CO_GRID_COLS = { ...DEFAULT_COLS, stock: false };
// [field, label, gm field, default gm]
const OTHER_COSTS = [["equipment", "Equipment", "equipment_gm", 15], ["travel", "Travel", "travel_gm", 15], ["disposal", "Disposal", "disposal_gm", 15], ["drive_time", "Drive time", "drive_time_gm", 0]];
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { FoundationSyncPill, isSyncLocked } from "@/components/integrations/FoundationSyncPill";
import { COUnlockDialog, useCanUnlockSync } from "@/components/integrations/COUnlockDialog";
import { getSettings } from "@/lib/api";
import { generateCOPdf } from "@/lib/coPdf";
import { TaxOverrideInline, useCanOverrideTax } from "@/components/common/TaxOverrideDialog";
import { TaxOverrideBadge } from "@/components/common/TaxOverrideBadge";
import { btn } from "@/components/common/btn";

// ── Shared calc helpers (mirrors Proposals.jsx) ──
const formatCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const formatCurrencyWhole = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const newLineItem = (frtGM = 15) => ({
  id: crypto.randomUUID(), name: "", description: "", part_number: "", manufacturer: "", qty: 1,
  material_cost: 0, material_margin: 50, freight: 0, freight_type: "unit", freight_margin: frtGM,
  labor_cost: 40, labor_margin: 73.333, labor_hours: 0, location: "",
});

const newScope = (name = "") => ({
  id: crypto.randomUUID(), name, line_items: [],
  travel: 0, travel_gm: 15, equipment: 0, equipment_gm: 15, disposal: 0, disposal_gm: 15,
});

const partToLineItem = (part, frtGM = 15) => ({
  id: crypto.randomUUID(), name: part.name || "", description: part.description || "",
  part_number: part.part_number || "", manufacturer: part.manufacturer || "", qty: 1,
  material_cost: part.part_cost || 0, material_margin: part.material_margin ?? 50,
  freight: part.freight || 0, freight_type: "unit", freight_margin: part.freight_margin ?? frtGM,
  labor_cost: part.labor_cost ?? 40, labor_margin: part.labor_margin ?? 73.333,
  labor_hours: part.labor_hours || 0, location: "",
  pricing_mode: part.pricing_mode || "standard",
  flat_rate: parseFloat(part.flat_rate) || 0,
  subcontract_cost: parseFloat(part.subcontract_cost) || 0,
});

const calcLineItemTotals = (item) => {
  // iter235 — flat-rate service items: unit sell = flat_rate exactly (labor bucket).
  // Window Care subcontract lines price the same way (sell = flat_rate/unit).
  if (item.pricing_mode === "flat_rate" || item.pricing_mode === "subcontract") {
    const flat = parseFloat(item.flat_rate) || 0;
    const hours = parseFloat(item.labor_hours) || 0;
    const labC = parseFloat(item.labor_cost) || 0;
    const freightCost = parseFloat(item.freight) || 0;
    const frtGM0 = parseFloat(item.freight_margin) || 0;
    const freight = (frtGM0 > 0 && frtGM0 < 100) ? freightCost / (1 - frtGM0 / 100) : freightCost;
    const freightType = item.freight_type || "unit";
    const qty = parseInt(item.qty) || 1;
    const unitTotal = flat + (freightType === "unit" ? freight : 0);
    const grand = unitTotal * qty + (freightType === "net" ? freight : 0);
    const totalFreightForItem = freightType === "unit" ? freight * qty : freight;
    const totalFreightCostForItem = freightType === "unit" ? freightCost * qty : freightCost;
    return { totalMat: 0, totalLab: flat, rate: 0, freight, freightType, unitTotal, grand, qty, hours, totalFreightForItem, totalFreightCostForItem, matC: 0, labC };
  }
  const matC = parseFloat(item.material_cost) || 0;
  const matGM = parseFloat(item.material_margin) || 0;
  const totalMat = matGM >= 100 ? matC : matC / (1 - matGM / 100);
  const labC = parseFloat(item.labor_cost) || 0;
  const labGM = parseFloat(item.labor_margin) || 0;
  const rate = labGM >= 100 ? labC : Math.ceil(labC / (1 - labGM / 100));
  const hours = parseFloat(item.labor_hours) || 0;
  const totalLab = hours * rate;
  const freightCost = parseFloat(item.freight) || 0;
  const frtGM = parseFloat(item.freight_margin) || 0;
  const freight = (frtGM > 0 && frtGM < 100) ? freightCost / (1 - frtGM / 100) : freightCost;
  const freightType = item.freight_type || "unit";
  const qty = parseInt(item.qty) || 1;
  const unitTotal = totalMat + (freightType === "unit" ? freight : 0) + totalLab;
  const grand = unitTotal * qty + (freightType === "net" ? freight : 0);
  const totalFreightForItem = freightType === "unit" ? freight * qty : freight;
  const totalFreightCostForItem = freightType === "unit" ? freightCost * qty : freightCost;
  return { totalMat, totalLab, rate, freight, freightType, unitTotal, grand, qty, hours, totalFreightForItem, totalFreightCostForItem, matC, labC };
};

const calcScopeCost = (cost, gmPct) => {
  const c = parseFloat(cost) || 0;
  const gm = parseFloat(gmPct) || 0;
  return c > 0 ? (gm >= 100 ? c : c / (1 - gm / 100)) : 0;
};

// Convert scopes → WO-style line items + budget (mirrors server.py convert logic)
function convertScopesToWOData(scopes) {
  const lineItems = [];
  const budget = {
    material: { cost: 0, revenue: 0 }, labor: { cost: 0, revenue: 0 },
    freight: { cost: 0, revenue: 0 }, equipment: { cost: 0, revenue: 0 },
    travel: { cost: 0, revenue: 0 }, disposal: { cost: 0, revenue: 0 },
    subcontract: { cost: 0, revenue: 0 },
  };
  let subtotal = 0;

  (scopes || []).forEach(scope => {
    const scopeName = scope.name || "";
    (scope.line_items || []).forEach(item => {
      const qty = parseInt(item.qty) || 1;
      // Window Care / subcontract pass-through → cost-type 3 line carrying the sub cost.
      if (item.pricing_mode === "subcontract") {
        const price = parseFloat(item.flat_rate) || 0;
        const subCost = parseFloat(item.subcontract_cost) || 0;
        const desc = scopeName ? `${scopeName}: ${item.name || ""}` : (item.name || "");
        const liTotal = Math.round(price * qty * 100) / 100;
        lineItems.push({ id: crypto.randomUUID(), description: desc, cost_type: "subcontract", quantity: qty, unit_price: Math.round(price * 100) / 100, total: liTotal, pricing_mode: "subcontract", flat_rate: price, subcontract_cost: subCost });
        budget.subcontract.cost += Math.round(subCost * qty * 100) / 100;
        budget.subcontract.revenue += liTotal;
        subtotal += liTotal;
        return;
      }
      // iter235 — flat-rate service items → single labor-classed line at flat_rate/unit.
      if (item.pricing_mode === "flat_rate") {
        const flat = parseFloat(item.flat_rate) || 0;
        const frHrs = parseFloat(item.labor_hours) || 0;
        const frLabC = parseFloat(item.labor_cost) || 0;
        const desc = scopeName ? `${scopeName}: ${item.name || ""}` : (item.name || "");
        const liTotal = Math.round(flat * qty * 100) / 100;
        lineItems.push({ id: crypto.randomUUID(), description: desc, cost_type: "labor", quantity: qty, unit_price: Math.round(flat * 100) / 100, total: liTotal, pricing_mode: "flat_rate", flat_rate: flat, labor_hours: frHrs, labor_cost: frLabC });
        budget.labor.cost += Math.round(frLabC * frHrs * qty * 100) / 100;
        budget.labor.revenue += liTotal;
        subtotal += liTotal;
        return;
      }
      const matC = parseFloat(item.material_cost) || 0;
      const matGM = parseFloat(item.material_margin) || 0;
      const matBilled = matGM >= 100 ? matC : (matC > 0 ? matC / (1 - matGM / 100) : 0);
      if (matC > 0) {
        const desc = scopeName ? `${scopeName}: ${item.name || ""}` : (item.name || "");
        const liTotal = Math.round(matBilled * qty * 100) / 100;
        lineItems.push({ id: crypto.randomUUID(), description: desc, cost_type: "material", quantity: qty, unit_price: Math.round(matBilled * 100) / 100, total: liTotal });
        budget.material.cost += Math.round(matC * qty * 100) / 100;
        budget.material.revenue += liTotal;
        subtotal += liTotal;
      }
      const labC = parseFloat(item.labor_cost) || 0;
      const labGM = parseFloat(item.labor_margin) || 0;
      const labRate = labGM >= 100 ? labC : (labC > 0 ? Math.ceil(labC / (1 - labGM / 100)) : 0);
      const hours = parseFloat(item.labor_hours) || 0;
      if (hours > 0 && labRate > 0) {
        const desc = scopeName ? `${scopeName}: ${item.name || ""} (labor)` : `${item.name || ""} (labor)`;
        const totalHours = hours * qty;
        const liTotal = Math.round(labRate * totalHours * 100) / 100;
        lineItems.push({ id: crypto.randomUUID(), description: desc, cost_type: "labor", quantity: totalHours, unit_price: labRate, total: liTotal });
        budget.labor.cost += Math.round(labC * hours * qty * 100) / 100;
        budget.labor.revenue += liTotal;
        subtotal += liTotal;
      }
      const freightCost = parseFloat(item.freight) || 0;
      const frtGM = parseFloat(item.freight_margin) || 0;
      const freight = (frtGM > 0 && frtGM < 100) ? freightCost / (1 - frtGM / 100) : freightCost;
      const freightType = item.freight_type || "unit";
      if (freight > 0) {
        const desc = scopeName ? `${scopeName}: ${item.name || ""} (freight)` : `${item.name || ""} (freight)`;
        const freightTotal = freightType === "unit" ? freight * qty : freight;
        const freightCostTotal = freightType === "unit" ? freightCost * qty : freightCost;
        lineItems.push({ id: crypto.randomUUID(), description: desc, cost_type: "freight", quantity: freightType === "unit" ? qty : 1, unit_price: Math.round(freight * 100) / 100, total: Math.round(freightTotal * 100) / 100 });
        budget.freight.cost += Math.round(freightCostTotal * 100) / 100;
        budget.freight.revenue += Math.round(freightTotal * 100) / 100;
        subtotal += freightTotal;
      }
    });
    // Scope-level costs
    const eqCost = parseFloat(scope.equipment) || 0;
    const eqGM = parseFloat(scope.equipment_gm) || 15;
    if (eqCost > 0) {
      const eqBilled = calcScopeCost(eqCost, eqGM);
      const desc = scopeName ? `${scopeName}: Equipment` : "Equipment";
      lineItems.push({ id: crypto.randomUUID(), description: desc, cost_type: "equipment", quantity: 1, unit_price: Math.round(eqBilled * 100) / 100, total: Math.round(eqBilled * 100) / 100 });
      budget.equipment.cost += Math.round(eqCost * 100) / 100;
      budget.equipment.revenue += Math.round(eqBilled * 100) / 100;
      subtotal += eqBilled;
    }
    const trCost = parseFloat(scope.travel) || 0;
    const trGM = parseFloat(scope.travel_gm) || 15;
    if (trCost > 0) {
      const trBilled = calcScopeCost(trCost, trGM);
      const desc = scopeName ? `${scopeName}: Travel` : "Travel";
      lineItems.push({ id: crypto.randomUUID(), description: desc, cost_type: "travel", quantity: 1, unit_price: Math.round(trBilled * 100) / 100, total: Math.round(trBilled * 100) / 100 });
      budget.travel.cost += Math.round(trCost * 100) / 100;
      budget.travel.revenue += Math.round(trBilled * 100) / 100;
      subtotal += trBilled;
    }
    const dispCost = parseFloat(scope.disposal) || 0;
    const dispGM = parseFloat(scope.disposal_gm) || 15;
    if (dispCost > 0) {
      const dispBilled = calcScopeCost(dispCost, dispGM);
      const desc = scopeName ? `${scopeName}: Disposal` : "Disposal";
      lineItems.push({ id: crypto.randomUUID(), description: desc, cost_type: "disposal", quantity: 1, unit_price: Math.round(dispBilled * 100) / 100, total: Math.round(dispBilled * 100) / 100 });
      budget.disposal.cost += Math.round(dispCost * 100) / 100;
      budget.disposal.revenue += Math.round(dispBilled * 100) / 100;
      subtotal += dispBilled;
    }
    // Drive time — categorized as LABOR (not taxed, rolls up with labor)
    const dtCost = parseFloat(scope.drive_time) || 0;
    const dtGM = parseFloat(scope.drive_time_gm) || 0;
    if (dtCost > 0) {
      const dtBilled = calcScopeCost(dtCost, dtGM);
      const desc = scopeName ? `${scopeName}: Drive Time` : "Drive Time";
      lineItems.push({ id: crypto.randomUUID(), description: desc, cost_type: "labor", quantity: 1, unit_price: Math.round(dtBilled * 100) / 100, total: Math.round(dtBilled * 100) / 100 });
      budget.labor.cost += Math.round(dtCost * 100) / 100;
      budget.labor.revenue += Math.round(dtBilled * 100) / 100;
      subtotal += dtBilled;
    }
  });

  return { lineItems, budget, subtotal: Math.round(subtotal * 100) / 100 };
}

// ── Library Search Dropdown ──
// ── CO Preview Modal (customer-facing view) ──
function COPreviewModal({ co, coSubtotal, coMaterialSubtotal, coTaxRate, coTaxAmount, coTotal, coTaxStale, woNumber, woTitle, originalContractTotal, priorApprovedTotal, onClose }) {
  const lineItems = co.line_items || [];
  // Item 11 — Contract summary block. Original + Prior approved COs +
  // This CO = Revised contract value. Hidden when no original total has
  // been threaded through (e.g. legacy code path).
  const showContractSummary = originalContractTotal != null;
  const revisedTotal = (originalContractTotal || 0) + (priorApprovedTotal || 0) + (coTotal || 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-white w-[816px] min-h-[400px] shadow-2xl rounded-lg" onClick={e => e.stopPropagation()} data-testid="co-preview-modal">
        {/* Close bar */}
        <div className="sticky top-0 z-10 bg-amber-700 text-white px-6 py-2 rounded-t-lg flex justify-between items-center">
          <span className="text-sm font-medium">Change Order Preview — {co.co_number}</span>
          <button onClick={onClose} className="text-white hover:text-amber-200"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-12 py-10" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">CHANGE ORDER</h1>
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-lg font-bold text-amber-700">{co.co_number}</span>
              </div>
              <p className="text-sm text-slate-600 mt-1">{co.description || ""}</p>
            </div>
            <div className="text-right text-xs text-slate-500 leading-relaxed">
              <div className="font-semibold text-slate-700 text-sm">{COMPANY_INFO.name}</div>
              <div>{COMPANY_INFO.fullAddress}</div>
              <div>Phone: {COMPANY_INFO.phone}</div>
              <div>Email: {COMPANY_INFO.email}</div>
            </div>
          </div>

          {/* Reference */}
          {(woNumber || woTitle) && (
            <div className="mb-4 text-sm">
              <span className="text-slate-400 text-xs uppercase tracking-wide">Reference Work Order</span>
              <br /><span className="font-semibold text-slate-700">{woNumber} — {woTitle}</span>
            </div>
          )}

          <hr className="border-slate-200 mb-6" />

          {/* Scope of Work */}
          <h3 className="text-sm font-bold uppercase text-slate-700 mb-3 tracking-wide">Scope of Work</h3>
          {lineItems.length > 0 ? (
            <table className="w-full text-xs border-collapse mb-4">
              <thead>
                <tr className="bg-amber-700 text-white">
                  <th className="text-left px-3 py-2 font-semibold">Description</th>
                  <th className="text-center px-3 py-2 font-semibold w-16">Qty</th>
                  <th className="text-right px-3 py-2 font-semibold w-28">Unit Price</th>
                  <th className="text-right px-3 py-2 font-semibold w-28">Total</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, idx) => (
                  <tr key={li.id || idx} className={idx % 2 === 0 ? "bg-white" : "bg-amber-50/30"}>
                    <td className="px-3 py-2 text-slate-700">{li.description || "—"}</td>
                    <td className="px-3 py-2 text-center">{li.quantity}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(li.unit_price)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{formatCurrency((li.quantity || 0) * (li.unit_price || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-slate-400 py-4 text-center mb-4">No line items</p>
          )}

          {/* Total */}
          <div className="flex justify-end">
            <div className="w-64 border-t-2 border-amber-700 pt-3 space-y-1">
              <div className="flex justify-between text-sm text-slate-600">
                <span>Subtotal</span>
                <span className="tabular-nums">{formatCurrency(coSubtotal || 0)}</span>
              </div>
              {((coTaxRate > 0 && coMaterialSubtotal !== 0) || co.tax_override) && (
                <div className="flex justify-between text-sm text-slate-600">
                  <span>{co.tax_override?.type === "amount" ? "Tax" : `Tax (${coTaxRate}% on material)`}</span>
                  <span className="tabular-nums">{formatCurrency(coTaxAmount)}</span>
                </div>
              )}
              {co.tax_override && (
                <div className="flex justify-end" data-testid={`co-preview-tax-override-${co.id}`}>
                  <TaxOverrideBadge override={co.tax_override} stale={coTaxStale} canManage={false} testId={`co-preview-tax-badge-${co.id}`} />
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-slate-800 pt-1 border-t border-slate-200">
                <span>Total</span>
                <span className="tabular-nums">{formatCurrency(coTotal)}</span>
              </div>
            </div>
          </div>

          {/* Contract Summary — Original + Prior COs + This CO = Revised */}
          {showContractSummary && (
            <div className="mt-8 rounded-lg border border-slate-300 bg-slate-50 p-5" data-testid="co-contract-summary">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Contract Summary</h4>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-slate-600">Original Contract Value</span>
                <span className="text-right tabular-nums text-slate-700" data-testid="contract-original">{formatCurrency(originalContractTotal || 0)}</span>

                <span className="text-slate-600">Prior Approved Change Orders</span>
                <span className="text-right tabular-nums text-slate-700" data-testid="contract-prior-cos">{formatCurrency(priorApprovedTotal || 0)}</span>

                <span className="text-slate-600">This Change Order ({co.co_number || "—"})</span>
                <span className="text-right tabular-nums text-slate-700" data-testid="contract-this-co">{formatCurrency(coTotal || 0)}</span>

                <span className="col-span-2 border-t border-slate-300 my-1" />

                <span className="font-bold text-slate-900">Revised Contract Value</span>
                <span className="text-right tabular-nums font-bold text-slate-900 text-base" data-testid="contract-revised">{formatCurrency(revisedTotal)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════
// Main COBuilder Component
// ══════════════════════════════════════════════
export default function COBuilder({ co, onChange, onRemove, onSave, onSubmit, onRevert, onApprove, onUnapprove, onNotApprove, onUnlocked, masterScopes, masterParts, isEditable, woId, woNumber, woTitle, woSiteId, clientName, siteName, siteAddress, taxRate, taxAuthority, hideTax = false, originalContractTotal, priorApprovedTotal }) {
  const [expandedScopes, setExpandedScopes] = useState({});
  // iter233 — settings-driven default freight GM% for NEW CO line items.
  const [defaultFrtGM, setDefaultFrtGM] = useState(15);
  useEffect(() => {
    getSettings().then(r => {
      const f = parseFloat(r.data?.default_freight_margin);
      if (Number.isFinite(f) && f >= 0 && f < 100) setDefaultFrtGM(f);
    }).catch(() => {});
  }, []);
  // Default: drafts expanded (newly added), submitted/approved/declined collapsed
  const [expanded, setExpanded] = useState((co.status || "draft") === "draft");
  // Item E — listen for global expand/collapse-all so each CO flips with
  // the rest of the WO sections.
  useEffect(() => {
    const onAll = (e) => setExpanded(!e.detail?.collapsed);
    window.addEventListener("wo:section-toggle-all", onAll);
    return () => window.removeEventListener("wo:section-toggle-all", onAll);
  }, []);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [unapproving, setUnapproving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // useMemo to stabilize the `scopes` identity so the totals memo
  // below doesn't recompute every render when `co` references change
  // but the underlying scopes array hasn't.
  const scopes = useMemo(() => co.scopes || [], [co.scopes]);
  const status = co.status || "draft";
  const isDraft = status === "draft";
  const canEdit = isEditable && isDraft;
  // Staged / imported in Foundation → read-only for everyone; only Admin / Accounting may unlock (purge → revise → re-send).
  const syncLocked = isSyncLocked(co.foundation_sync);
  const canUnlock = useCanUnlockSync();
  const [unlockOpen, setUnlockOpen] = useState(false);

  const coTaxRate = taxRate || 0;

  const toggleScope = (id) => setExpandedScopes(p => ({ ...p, [id]: !p[id] }));

  const handleSave = async () => {
    if (!woId || !onSave) return;
    setSaving(true);
    try {
      await onSave(woId);
      // Parent (WorkOrderTab) already toasts on success/failure; keep this
      // silent so we don't double-toast.
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const doc = await generateCOPdf({
        co,
        woNumber, woTitle,
        clientName, siteName, siteAddress,
        taxRate: coTaxRate,
        coSubtotal, coTaxAmount, coTotal,
        hideTax: !!(co?.hide_tax ?? hideTax),
        originalContractTotal, priorApprovedTotal,
      });
      // jsPDF `.save()` triggers a browser download — lands in the
      // user's Downloads folder just like the WO PDF button does.
      doc.save(`${co.co_number || "ChangeOrder"}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleSubmit = async () => {
    if (!woId || !co.id) return;
    setSubmitting(true);
    try {
      // Pass live-computed coTotal up — parent uses it in the email
      // dialog so the amount is never stale even if the CO was never
      // saved. Parent opens the COEmailDialog on success; the mailto
      // fires from the dialog's Send button (fresh user gesture).
      await onSubmit(woId, co.id, coTotal);
      toast.success("CO submitted");
    } catch (e) {
      toast.error("Failed to submit CO");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevert = async () => {
    if (!woId || !co.id) return;
    setReverting(true);
    try {
      await onRevert(woId, co.id);
      toast.success("CO reverted to draft");
    } catch (e) {
      toast.error("Failed to revert CO");
    } finally {
      setReverting(false);
    }
  };

  const handleApprove = async () => {
    if (!woId || !co.id) return;
    setApproving(true);
    try {
      const res = await onApprove(woId, co.id);
      toast.success(res?.message || "Approved — Foundation push queued", { duration: 6000 });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? (String(detail).startsWith("Not approved") ? detail : `Not approved: ${detail}`) : "Not approved: the server could not be reached — nothing was changed", { duration: 8000 });
    } finally {
      setApproving(false);
    }
  };

  // ── Scope CRUD ──
  const addScope = (name = "") => {
    const s = newScope(name);
    setExpandedScopes(p => ({ ...p, [s.id]: true }));
    updateScopes([...scopes, s]);
  };

  const removeScope = (scopeId) => {
    updateScopes(scopes.filter(s => s.id !== scopeId));
  };

  const updateScopeField = (scopeId, field, value) => {
    updateScopes(scopes.map(s => s.id === scopeId ? { ...s, [field]: value } : s));
  };

  // ── Line Item CRUD ──
  const addLineItem = (scopeId) => {
    updateScopes(scopes.map(s => s.id === scopeId ? { ...s, line_items: [...s.line_items, newLineItem(defaultFrtGM)] } : s));
  };

  const addLineItemFromPart = (scopeId, part) => {
    updateScopes(scopes.map(s => s.id === scopeId ? { ...s, line_items: [...s.line_items, partToLineItem(part, defaultFrtGM)] } : s));
  };

  const updateLineItem = (scopeId, itemId, field, value) => {
    updateScopes(scopes.map(s => {
      if (s.id !== scopeId) return s;
      return { ...s, line_items: s.line_items.map(li => li.id === itemId ? { ...li, [field]: value } : li) };
    }));
  };

  const removeLineItem = (scopeId, itemId) => {
    updateScopes(scopes.map(s => {
      if (s.id !== scopeId) return s;
      return { ...s, line_items: s.line_items.filter(li => li.id !== itemId) };
    }));
  };

  const addLineItemsFromParts = (scopeId, parts) => {
    updateScopes(scopes.map(s => s.id === scopeId ? { ...s, line_items: [...s.line_items, ...parts.map(p => partToLineItem(p, defaultFrtGM))] } : s));
  };

  const duplicateLineItem = (scopeId, itemId) => {
    updateScopes(scopes.map(s => {
      if (s.id !== scopeId) return s;
      const idx = s.line_items.findIndex(li => li.id === itemId);
      if (idx < 0) return s;
      const copy = { ...s.line_items[idx], id: crypto.randomUUID(), wc_key: undefined };
      return { ...s, line_items: [...s.line_items.slice(0, idx + 1), copy, ...s.line_items.slice(idx + 1)] };
    }));
  };

  const addImportedLines = (scopeId, items, meta = {}) => {
    const lines = items.map((it) => {
      const part = it.part_number && (masterParts || []).find((p) => p.part_number && p.part_number.toLowerCase() === it.part_number.toLowerCase());
      const base = part ? partToLineItem(part, defaultFrtGM) : newLineItem(defaultFrtGM);
      return { ...base, name: it.name || base.name, part_number: it.part_number || base.part_number, description: it.description || base.description,
        qty: parseInt(it.qty) || 1, material_cost: parseFloat(it.unit_cost) || 0, manufacturer: base.manufacturer || meta.vendor || "" };
    });
    updateScopes(scopes.map(s => s.id === scopeId ? { ...s, line_items: [...s.line_items, ...lines] } : s));
    toast.success(`${lines.length} line${lines.length === 1 ? "" : "s"} added from quote`);
  };

  const saveLineItemToLibrary = async (scope, item) => {
    const matchingScope = (masterScopes || []).find(ms => ms.name.toLowerCase() === (scope.name || "").toLowerCase());
    if (!matchingScope) { toast.error(`Scope "${scope.name}" not found in library. Create it in Equipment Library first.`); return; }
    try {
      await createMasterPart({
        scope_id: matchingScope.id, equipment_id: matchingScope.id, name: item.name, description: item.description || "",
        part_cost: parseFloat(item.material_cost) || 0, material_margin: parseFloat(item.material_margin) || 50,
        freight: parseFloat(item.freight) || 0, labor_hours: parseFloat(item.labor_hours) || 0,
        labor_cost: parseFloat(item.labor_cost) || 40, labor_margin: parseFloat(item.labor_margin) || 73.333,
        ...(item.pricing_mode === "flat_rate" ? { pricing_mode: "flat_rate", flat_rate: parseFloat(item.flat_rate) || 0 } : {}),
      });
      toast.success(`"${item.name}" saved to library`);
    } catch { toast.error("Failed to save to library"); }
  };

  // Update scopes and auto-compute line_items + budget
  const updateScopes = (newScopes) => {
    const { lineItems, budget, subtotal } = convertScopesToWOData(newScopes);
    onChange(co.id, {
      scopes: newScopes,
      line_items: lineItems,
      budget,
      subtotal,
    });
  };

  const getPartsForScopeName = useCallback((scopeName) => {
    if (!scopeName || !masterScopes) return [];
    const matchingScope = masterScopes.find(ms => ms.name.toLowerCase() === scopeName.toLowerCase());
    if (!matchingScope) return [];
    return (masterParts || []).filter(p => p.scope_id === matchingScope.id);
  }, [masterScopes, masterParts]);

  // Compute CO subtotal from scopes. Tax applies ONLY to material revenue.
  const coLineItems = co.line_items;
  const { coSubtotal, coMaterialSubtotal } = useMemo(() => {
    let total = 0;
    let materialTotal = 0;
    if (scopes.length === 0 && (coLineItems || []).length > 0) {
      // API-built / legacy COs carry flat line_items only.
      (coLineItems || []).forEach(li => {
        const ext = (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);
        total += ext;
        const ct = String(li.cost_type || "").toLowerCase();
        if (ct === "2" || ct === "material") materialTotal += ext;
      });
      return { coSubtotal: total, coMaterialSubtotal: materialTotal };
    }
    scopes.forEach(scope => {
      (scope.line_items || []).forEach(item => {
        const t = calcLineItemTotals(item);
        total += t.grand;
        materialTotal += t.totalMat * t.qty;
      });
      total += calcScopeCost(scope.equipment, scope.equipment_gm ?? 15);
      total += calcScopeCost(scope.travel, scope.travel_gm ?? 15);
      total += calcScopeCost(scope.disposal, scope.disposal_gm ?? 15);
      total += calcScopeCost(scope.drive_time, scope.drive_time_gm ?? 0);
    });
    if (total === 0 && (co.status || "draft") !== "draft" && parseFloat(co.subtotal) ) {
      return { coSubtotal: parseFloat(co.subtotal) || 0, coMaterialSubtotal: 0 };
    }
    return { coSubtotal: total, coMaterialSubtotal: materialTotal };
  }, [scopes, coLineItems, co.status, co.subtotal]);
  const hasPricing = scopes.length > 0 || (coLineItems || []).length > 0;
  // iter306 — manual CO tax (rate or exact $) wins over the inherited WO rate.
  const coTaxOverride = co.tax_override || null;
  const coTaxAmount = coTaxOverride?.type === "rate"
    ? coMaterialSubtotal * ((parseFloat(coTaxOverride.rate) || 0) / 100)
    : coTaxOverride?.type === "amount"
      ? (parseFloat(coTaxOverride.amount) || 0)
      : coMaterialSubtotal * (coTaxRate / 100);
  const coTaxStale = coTaxOverride?.type === "amount" && coTaxOverride.basis_taxable != null
    && Math.abs(Number(coTaxOverride.basis_taxable) - coMaterialSubtotal) > 0.01;
  const coDisplayRate = coTaxOverride?.type === "rate" ? (parseFloat(coTaxOverride.rate) || 0) : coTaxRate;
  const coTotal = coSubtotal + coTaxAmount;
  const canOverrideTax = useCanOverrideTax();
  const [taxOverrideOpen, setTaxOverrideOpen] = useState(false);
  const openTaxOverride = async () => {
    if (isDraft && onSave && woId) await onSave(woId, { skipReload: true, silent: true });
    setTaxOverrideOpen(true);
  };
  const applyTaxOverrideResult = (res) => {
    if (res?.co) onChange(co.id, { tax_override: res.co.tax_override || null });
  };

  const STATUS_BADGE = {
    draft: "bg-slate-100 text-slate-600 border-slate-300",
    submitted: "bg-yellow-100 text-yellow-700 border-yellow-300",
    approved: "bg-green-100 text-green-700 border-green-300",
    declined: "bg-red-100 text-red-600 border-red-300",
    not_approved: "bg-red-100 text-red-600 border-red-300",
  };
  const STATUS_LABEL = { draft: "Draft", submitted: "Submitted", approved: "Approved", declined: "Declined", not_approved: "Not Approved" };

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid={`change-order-${co.id}`}>
      {/* CO Header */}
      <div className="px-3 py-2 flex items-center justify-between gap-2 flex-wrap cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setExpanded(!expanded)} data-testid={`co-toggle-${co.id}`}>
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <span className="font-mono text-sm font-medium text-slate-800">{co.co_number}</span>
          <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[status]}`}>{STATUS_LABEL[status]}</Badge>
          {canEdit ? (
            <Input
              value={co.description}
              onChange={e => { e.stopPropagation(); onChange(co.id, { description: e.target.value }); }}
              onClick={e => e.stopPropagation()}
              placeholder="Change order description"
              className="h-7 text-xs w-64 bg-white"
            />
          ) : (
            <span className="text-sm font-medium">{co.description || "No description"}</span>
          )}
        </div>
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <span className={`text-xs font-semibold num ${coTotal < 0 ? "text-red-700" : "text-slate-800"}`} data-testid={`co-total-${co.id}`}>{formatCurrency(coTotal)}</span>
          {coTotal < 0 && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-300" data-testid={`co-credit-badge-${co.id}`}>Credit / deduct</Badge>}
          {coTaxOverride ? (
            <span className="text-[10px] text-amber-700 font-semibold" data-testid={`co-tax-manual-chip-${co.id}`}>manual tax</span>
          ) : (coTaxRate > 0 && coMaterialSubtotal !== 0 && <span className="text-[10px] text-slate-400">({coTaxRate}% tax on material)</span>)}

          {/* Status action buttons */}
          {scopes.length > 0 && (
            <button type="button" className={btn("nav")}
              onClick={() => setShowPreview(true)} data-testid={`btn-view-co-${co.id}`}>
              <Eye className="w-3 h-3" /> View
            </button>
          )}
          {scopes.length > 0 && (
            <button type="button" className={btn("nav")}
              onClick={handleDownloadPDF} disabled={downloading} data-testid={`btn-download-co-pdf-${co.id}`}>
              {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
              PDF
            </button>
          )}
          {isDraft && isEditable && (
            <button type="button" className={btn("write")}
              onClick={handleSave} disabled={saving} data-testid={`btn-save-co-${co.id}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </button>
          )}
          {isDraft && isEditable && scopes.length > 0 && (
            <button type="button" className={btn("data")}
              onClick={handleSubmit} disabled={submitting} data-testid={`btn-submit-co-${co.id}`}>
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              Email CO
            </button>
          )}
          {status === "submitted" && isEditable && (
            <button type="button" className={btn("nav")}
              onClick={async () => { if (onNotApprove) { await onNotApprove(woId, co.id); } }} data-testid={`btn-not-approve-co-${co.id}`}>
              <XCircle className="w-3 h-3" /> Not approved
            </button>
          )}
          {(status === "submitted" || (isDraft && scopes.length > 0)) && isEditable && (
            <button type="button" className={btn("write")}
              onClick={handleApprove} disabled={approving} title={isDraft ? "Manually approve without emailing the customer" : "Approve this change order"} data-testid={`btn-approve-co-${co.id}`}>
              {approving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              Approve
            </button>
          )}
          {status === "approved" && co.executed_co_pdf && (
            <button type="button" className={btn("data")}
              onClick={() => {
                const link = document.createElement("a");
                link.href = co.executed_co_pdf;
                link.download = `${co.co_number}_Executed.pdf`;
                link.click();
              }} data-testid={`btn-view-executed-co-${co.id}`}>
              <FileDown className="w-3 h-3" /> Executed CO
            </button>
          )}
          {status === "approved" && isEditable && !syncLocked && (
            <button type="button" className={btn("nav")}
              onClick={async () => { setUnapproving(true); try { if (onUnapprove) await onUnapprove(woId, co.id); } finally { setUnapproving(false); } }} disabled={unapproving} data-testid={`btn-cancel-co-${co.id}`}>
              {unapproving ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Cancel approval
            </button>
          )}
          {syncLocked && canUnlock && woId && (
            <button type="button" className={btn("nav")}
              onClick={() => setUnlockOpen(true)} title="Purge it in Foundation first, then unlock here to revise and re-send" data-testid={`btn-unlock-co-${co.id}`}>
              <Unlock className="w-3 h-3" /> Unlock to revise
            </button>
          )}
          {/* Read-only Foundation state — accounting sends approved COs from Settings › Integrations › Foundation › Sync. */}
          {status === "approved" && (
            <FoundationSyncPill
              sync={co.foundation_sync || { status: "ready", message: "Approved — accounting sends it from Settings › Integrations › Foundation › Sync" }}
              testId={`co-foundation-sync-${co.id}`}
            />
          )}
          {(status === "submitted" || status === "declined" || status === "not_approved") && isEditable && (
            <button type="button" className={btn("nav")}
              onClick={handleRevert} disabled={reverting} data-testid={`btn-revise-co-${co.id}`}>
              {reverting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Revise
            </button>
          )}
          {isDraft && isEditable && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
              onClick={() => onRemove(co.id)} title="Remove change order" data-testid={`btn-remove-co-${co.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* CO Body */}
      {expanded && (
        <div className="p-3 space-y-3">
          {syncLocked && (
            <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 px-2.5 py-2 text-[11px] text-blue-900 dark:text-blue-100" data-testid={`co-locked-banner-${co.id}`}>
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold">Locked — {co.foundation_sync?.label || "synced to Foundation"}</span>
                {co.foundation_sync?.foundation_change_order_no && <> as change order <span className="font-mono">{co.foundation_sync.foundation_change_order_no}</span></>}
                {co.foundation_sync?.pushed_at && <> · sent {String(co.foundation_sync.pushed_at).slice(0, 10)}</>}.
                {" "}This change order is read-only.{" "}
                {canUnlock
                  ? <>To revise it, purge it in Foundation and use <b>Unlock to revise</b>.</>
                  : <>To revise it, ask Accounting to purge it in Foundation and unlock it here.</>}
              </div>
            </div>
          )}
          {/* Add Scope controls */}
          {canEdit && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">{scopes.length} scope{scopes.length !== 1 ? "s" : ""}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" data-testid={`btn-co-add-scope-${co.id}`}>
                    <Plus className="w-3 h-3 mr-1" /> Add Scope
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {(masterScopes || []).length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400">No scopes in library</div>
                  )}
                  {(masterScopes || []).map(ms => (
                    <DropdownMenuItem key={ms.id} onClick={() => addScope(ms.name)}>
                      {ms.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {scopes.length === 0 && (
            <div className="text-center py-4 text-slate-400 text-xs border rounded-lg border-dashed">
              No scopes. Click "Add Scope" to begin pricing this change order. Use a negative quantity on a line to deduct (credit).
            </div>
          )}

          {/* Scope grid — same spreadsheet as the proposal builder (negative qty = deduct). */}
          {scopes.length > 0 && (
            <fieldset disabled={!canEdit} className="min-w-0 border-0 p-0 m-0 disabled:opacity-90" data-testid={`co-grid-${co.id}`}>
              <EstimateGrid
                scopes={scopes} status={status} reviewable={false}
                expandedScopes={expandedScopes} toggleScope={toggleScope}
                toggleAllScopes={() => {
                  const allExpanded = scopes.every(s => expandedScopes[s.id]);
                  const next = {};
                  scopes.forEach(s => { next[s.id] = !allExpanded; });
                  setExpandedScopes(next);
                }}
                updateScope={updateScopeField} updateLineItem={updateLineItem} addLineItem={addLineItem}
                addLineItemFromPart={addLineItemFromPart} addLineItemsFromParts={addLineItemsFromParts}
                removeLineItem={removeLineItem} removeScope={removeScope} duplicateLineItem={duplicateLineItem}
                saveLineItemToLibrary={saveLineItemToLibrary} applyWindowCare={() => {}}
                applyLibraryToLine={() => {}} dismissLibraryForLine={() => {}} addImportedLines={addImportedLines}
                getPartsForScopeName={getPartsForScopeName} masterParts={masterParts || []} partsStockMap={{}}
                siteId={woSiteId} siteName={siteName} cols={CO_GRID_COLS} readOnly={!canEdit}
              />
            </fieldset>
          )}

          {/* Other costs per scope — equipment / travel / disposal / drive time (cost + GM%) */}
          {scopes.length > 0 && (canEdit || scopes.some(s => OTHER_COSTS.some(([k]) => parseFloat(s[k]) > 0))) && (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`co-other-costs-${co.id}`}>
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-200 bg-slate-50">
                <span className="text-[10.5px] font-medium tracking-wide text-slate-500 uppercase">Other costs</span>
                <span className="text-[10px] text-slate-400">cost → billed at GM% · drive time rolls into labor</span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10.5px] text-slate-400">
                    <th className="text-left px-3 py-1 font-medium">Scope</th>
                    {OTHER_COSTS.map(([k, label]) => <th key={k} className="text-right px-2 py-1 font-medium" colSpan={2}>{label}</th>)}
                    <th className="text-right px-3 py-1 font-medium">Billed</th>
                  </tr>
                </thead>
                <tbody>
                  {scopes.map((scope, si) => {
                    const billed = OTHER_COSTS.reduce((a, [k, , gmKey, gmDef]) => a + calcScopeCost(scope[k], scope[gmKey] ?? gmDef), 0);
                    return (
                      <tr key={scope.id} className="border-t border-slate-100" data-testid={`co-other-costs-row-${si}`}>
                        <td className="px-3 py-1 font-medium text-slate-700 truncate max-w-[160px]">{scope.name || `Scope ${si + 1}`}</td>
                        {OTHER_COSTS.map(([k, label, gmKey, gmDef]) => (
                          <Fragment key={k}>
                            <td className="px-1 py-0.5 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <input type="number" step="0.01" value={scope[k] || ""} disabled={!canEdit} placeholder="0"
                                  onChange={e => updateScopeField(scope.id, k, e.target.value)}
                                  className="h-6 w-20 rounded border border-slate-200 bg-white px-1 text-right text-xs tabular-nums outline-none focus:border-brand disabled:bg-slate-50"
                                  data-testid={`co-${k}-${si}`} title={`${label} cost`} />
                                {k === "drive_time" && canEdit && woSiteId && (
                                  <button type="button" className="text-[10px] font-medium text-brand hover:underline" title="Auto-calculate from the shop to the site"
                                    onClick={async () => {
                                      try {
                                        const { getDriveTimeForSite } = await import("@/lib/api");
                                        const r = await getDriveTimeForSite(woSiteId, true);
                                        const cost = r.data?.cost_amount || 0;
                                        updateScopes(scopes.map(sc => sc.id === scope.id ? { ...sc, drive_time: cost, drive_time_gm: cost > 0 ? 50 : 0 } : sc));
                                        toast.success(`Drive Time: Cost $${cost} → Billed $${r.data?.billed_amount || 0} (${(r.data?.miles_round_trip || 0).toFixed(0)} mi round-trip)`);
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || "Could not auto-calculate drive time");
                                      }
                                    }} data-testid={`co-drive-auto-${si}`}>Auto</button>
                                )}
                              </div>
                            </td>
                            <td className="px-1 py-0.5 text-right">
                              <input type="number" step="1" value={Math.round(parseFloat(scope[gmKey] ?? gmDef) || 0)} disabled={!canEdit}
                                onChange={e => updateScopeField(scope.id, gmKey, e.target.value)}
                                className="h-6 w-12 rounded border border-slate-200 bg-white px-1 text-right text-xs tabular-nums outline-none focus:border-brand disabled:bg-slate-50"
                                data-testid={`co-${gmKey}-${si}`} title={`${label} GM%`} />
                            </td>
                          </Fragment>
                        ))}
                        <td className="px-3 py-1 text-right font-semibold tabular-nums text-slate-800" data-testid={`co-other-billed-${si}`}>{formatCurrency(billed)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {woId && (
            <TaxOverrideInline open={taxOverrideOpen} onOpenChange={setTaxOverrideOpen} docType="change_order" docId={co.id} woId={woId}
              current={{ tax_rate: coTaxRate, tax_authority: co.tax_override?.tax_no || taxAuthority || "", taxable_base: coMaterialSubtotal, tax_amount: coMaterialSubtotal * (coTaxRate / 100), tax_override: coTaxOverride }}
              onApplied={applyTaxOverrideResult} />
          )}

          {/* CO Grand Total — Subtotal / Tax / Total */}
          {hasPricing && (
            <div className="flex justify-end pt-1">
              <div className="w-60 text-xs space-y-0.5">
                <div className="flex justify-between text-slate-600">
                  <span>CO Subtotal</span>
                  <span className="tabular-nums">{formatCurrency(coSubtotal)}</span>
                </div>
                {((coDisplayRate > 0 && coMaterialSubtotal !== 0) || coTaxOverride) && (
                  <div className="flex justify-between text-slate-600 items-center">
                    <span>{coTaxOverride?.type === "amount" ? "Tax (manual)" : `Tax (${coDisplayRate}% on material)`}</span>
                    <span className="tabular-nums" data-testid={`co-tax-amount-${co.id}`}>{formatCurrency(coTaxAmount)}</span>
                  </div>
                )}
                {canOverrideTax && woId && !syncLocked && (
                  <div className="flex justify-end">
                    <button type="button" className="text-[10px] text-amber-700 underline hover:text-amber-900" onClick={openTaxOverride} data-testid={`co-tax-override-btn-${co.id}`}>
                      {coTaxOverride ? "Change tax" : "Override tax"}
                    </button>
                  </div>
                )}
                {coTaxOverride && (
                  <TaxOverrideBadge override={coTaxOverride} stale={coTaxStale} docType="change_order" docId={co.id} woId={woId}
                    canManage={canOverrideTax} onChange={applyTaxOverrideResult} testId={`co-tax-override-badge-${co.id}`} />
                )}
                <div className="flex justify-between pt-1 border-t border-amber-300 text-sm font-bold text-amber-800">
                  <span>CO Total</span>
                  <span className="tabular-nums">{formatCurrency(coTotal)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CO Preview Modal */}
      {showPreview && (
        <COPreviewModal
          co={co}
          coSubtotal={coSubtotal}
          coMaterialSubtotal={coMaterialSubtotal}
          coTaxRate={coDisplayRate}
          coTaxAmount={coTaxAmount}
          coTotal={coTotal}
          coTaxStale={coTaxStale}
          woNumber={woNumber}
          woTitle={woTitle}
          originalContractTotal={originalContractTotal}
          priorApprovedTotal={priorApprovedTotal}
          onClose={() => setShowPreview(false)}
        />
      )}
      {unlockOpen && (
        <COUnlockDialog woId={woId} woNumber={woNumber} co={co} onClose={() => setUnlockOpen(false)}
          onUnlocked={(data) => onUnlocked?.(co.id, data)} />
      )}
    </div>
  );
}
